import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { api } from "@/lib/api";
import { useSuspendPollingWhileOpen } from "@/lib/dialogPolling";
import { toast } from "sonner";
import {
  X,
  Plus,
  Check,
  Pencil,
  Trash2,
  ChevronLeft,
  Share2,
  Printer,
  Link as LinkIcon,
  RefreshCw,
  GripVertical,
  Calendar as CalendarIcon,
} from "lucide-react";

/**
 * TodoListsSheet — Cozi-style shared lists.
 *
 * Two stacked views inside one sheet:
 *   1. "Lists index" — shows every list, item progress, big "+ New list",
 *      drag-to-reorder by the grip handle.
 *   2. "List detail" — shows items, lets you tick / edit / delete, tag
 *      family members, set due dates, drag-to-reorder.
 *
 * Anyone in the family can edit lists. We keep the perms simple here on
 * purpose — chores already model "assignee + parent override"; lists are
 * meant to be the family's shared scratchpad.
 */
export default function TodoListsSheet({ members = [], initialListId = null, onClose, onOpenLinkedEvent = null }) {
  useSuspendPollingWhileOpen();

  // Pets can't tick chores or own packing items; filter them out of the
  // tag picker so the dropdown stays calm.
  const taggable = useMemo(
    () => (members || []).filter((m) => m.role !== "pet"),
    [members]
  );

  const [lists, setLists] = useState(null);
  const [openListId, setOpenListId] = useState(initialListId || null);
  const [items, setItems] = useState(null);
  const [shareOpenFor, setShareOpenFor] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadLists = useCallback(async () => {
    try {
      const rows = await api.listTodoLists();
      setLists(rows);
    } catch {
      toast.error("Could not load lists");
    }
  }, []);

  const loadItems = useCallback(async (id) => {
    if (!id) return;
    try {
      const rows = await api.listTodoItems(id);
      setItems(rows);
    } catch {
      toast.error("Could not load that list");
    }
  }, []);

  useEffect(() => {
    loadLists();
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (openListId) {
        setOpenListId(null);
        setItems(null);
        setShareOpenFor(null);
      } else {
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [loadLists, openListId, onClose]);

  useEffect(() => {
    if (openListId) loadItems(openListId);
  }, [openListId, loadItems]);

  const openList = lists && openListId ? lists.find((l) => l.id === openListId) : null;

  // ---- Lists index handlers ---------------------------------------------

  const createList = async (title) => {
    const trimmed = (title || "").trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      const created = await api.createTodoList({ title: trimmed });
      setLists((prev) => (prev ? [...prev, created] : [created]));
      setOpenListId(created.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create list");
    } finally {
      setBusy(false);
    }
  };

  const renameList = async (id, title) => {
    const trimmed = (title || "").trim();
    if (!trimmed) return;
    try {
      const updated = await api.updateTodoList(id, { title: trimmed });
      setLists((prev) => prev.map((l) => (l.id === id ? { ...l, ...updated } : l)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not rename");
    }
  };

  const setListMembers = async (id, member_ids) => {
    // Optimistic update for snappy chip-tap feel — rollback on error.
    const prev = lists;
    setLists((p) => p.map((l) => (l.id === id ? { ...l, member_ids } : l)));
    try {
      const updated = await api.updateTodoList(id, { member_ids });
      setLists((p) => p.map((l) => (l.id === id ? { ...l, ...updated } : l)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't tag people");
      setLists(prev);
    }
  };

  const setListDueDate = async (id, due_date) => {
    const prev = lists;
    setLists((p) => p.map((l) => (l.id === id ? { ...l, due_date: due_date || null } : l)));
    try {
      // Server treats empty string as "clear", null isn't accepted by
      // the validator, so coerce to "" when the picker passes empty.
      const updated = await api.updateTodoList(id, { due_date: due_date || "" });
      setLists((p) => p.map((l) => (l.id === id ? { ...l, ...updated } : l)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't set due date");
      setLists(prev);
    }
  };

  const setListLinkedEvent = async (id, event_id) => {
    const prev = lists;
    setLists((p) => p.map((l) => (l.id === id ? { ...l, linked_event_id: event_id || null } : l)));
    try {
      const updated = await api.linkTodoListToEvent(id, event_id || "");
      setLists((p) => p.map((l) => (l.id === id ? { ...l, ...updated } : l)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't attach to event");
      setLists(prev);
    }
  };

  const deleteList = async (l) => {
    const ok = window.confirm(
      `Delete "${l.title}"? Items on this list will be removed for everyone.`
    );
    if (!ok) return;
    try {
      await api.deleteTodoList(l.id);
      setLists((prev) => prev.filter((x) => x.id !== l.id));
      if (openListId === l.id) {
        setOpenListId(null);
        setItems(null);
      }
    } catch {
      toast.error("Could not delete");
    }
  };

  const reorderLists = async (newOrder) => {
    const prev = lists;
    setLists(newOrder);
    try {
      await api.reorderTodoLists(newOrder.map((l) => l.id));
    } catch {
      toast.error("Could not save new order");
      setLists(prev);
    }
  };

  // ---- Item handlers ----------------------------------------------------

  const addItem = async ({ text, member_ids = [], due_date = null }) => {
    const trimmed = (text || "").trim();
    if (!trimmed || !openListId) return;
    try {
      const created = await api.createTodoItem(openListId, {
        text: trimmed,
        member_ids,
        due_date: due_date || null,
      });
      setItems((prev) => (prev ? [...prev, created] : [created]));
      setLists((prev) =>
        prev.map((l) =>
          l.id === openListId ? { ...l, item_count: (l.item_count || 0) + 1 } : l
        )
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add");
    }
  };

  const toggleItem = async (item) => {
    const next = !item.done;
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, done: next } : i)));
    setLists((prev) =>
      prev.map((l) =>
        l.id === openListId
          ? {
              ...l,
              done_count: Math.max(0, (l.done_count || 0) + (next ? 1 : -1)),
            }
          : l
      )
    );
    try {
      await api.updateTodoItem(openListId, item.id, { done: next });
    } catch {
      toast.error("Could not save");
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, done: !next } : i)));
      setLists((prev) =>
        prev.map((l) =>
          l.id === openListId
            ? { ...l, done_count: Math.max(0, (l.done_count || 0) + (next ? -1 : 1)) }
            : l
        )
      );
    }
  };

  const updateItem = async (item, patch) => {
    // Optimistic merge so the UI reflects the change instantly. The server
    // is the source of truth; if it rejects we re-fetch the list.
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, ...patch } : i)));
    try {
      const saved = await api.updateTodoItem(openListId, item.id, patch);
      setItems((prev) => prev.map((i) => (i.id === item.id ? saved : i)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
      loadItems(openListId);
    }
  };

  const deleteItem = async (item) => {
    setItems((prev) => prev.filter((i) => i.id !== item.id));
    setLists((prev) =>
      prev.map((l) =>
        l.id === openListId
          ? {
              ...l,
              item_count: Math.max(0, (l.item_count || 0) - 1),
              done_count: Math.max(0, (l.done_count || 0) - (item.done ? 1 : 0)),
            }
          : l
      )
    );
    try {
      await api.deleteTodoItem(openListId, item.id);
    } catch {
      toast.error("Could not delete");
      loadItems(openListId);
      loadLists();
    }
  };

  const reorderItems = async (newOrder) => {
    const prev = items;
    setItems(newOrder);
    try {
      await api.reorderTodoItems(openListId, newOrder.map((i) => i.id));
    } catch {
      toast.error("Could not save new order");
      setItems(prev);
    }
  };

  const rotateShare = async (l) => {
    try {
      const updated = await api.updateTodoList(l.id, { rotate_share: true });
      setLists((prev) => prev.map((x) => (x.id === l.id ? { ...x, ...updated } : x)));
      setShareOpenFor((prev) => (prev && prev.id === l.id ? { ...prev, ...updated } : prev));
      toast.success("New share link created");
    } catch {
      toast.error("Could not rotate link");
    }
  };

  // ---------------------------------------------------------------------

  return (
    <div
      className="fixed inset-0 z-50 bg-[color:var(--ink)]/45 backdrop-blur-sm flex items-stretch md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
      data-testid="todo-lists-sheet"
    >
      <div
        className="relative bg-[color:var(--bg)] md:rounded-[20px] w-full md:max-w-2xl max-h-[100vh] md:max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[color:var(--bg)] px-5 md:px-7 pt-5 pb-3 flex items-center justify-between border-b border-[color:var(--line-soft)] z-10">
          <div className="flex items-center gap-2 min-w-0">
            {openListId ? (
              <button
                onClick={() => {
                  setOpenListId(null);
                  setItems(null);
                  setShareOpenFor(null);
                }}
                className="w-9 h-9 rounded-full hover:bg-[color:var(--bg-soft)] flex items-center justify-center shrink-0"
                aria-label="Back to all lists"
                data-testid="todo-back-btn"
              >
                <ChevronLeft className="w-5 h-5" strokeWidth={2} />
              </button>
            ) : null}
            <div className="min-w-0">
              <div className="label-tiny">Shared</div>
              <h2
                className="font-display-italic text-3xl md:text-4xl leading-none mt-1 tracking-tight truncate"
                data-testid="todo-sheet-title"
              >
                {openList ? openList.title : "Lists"}
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-[color:var(--bg-soft)] flex items-center justify-center shrink-0"
            aria-label="Close"
            data-testid="todo-close-btn"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        <div className="px-5 md:px-7 py-5 space-y-5">
          {!openListId ? (
            <ListsIndex
              lists={lists}
              busy={busy}
              onCreate={createList}
              onOpen={(id) => setOpenListId(id)}
              onRename={renameList}
              onDelete={deleteList}
              onShare={(l) => setShareOpenFor(l)}
              onReorder={reorderLists}
              taggable={taggable}
            />
          ) : (
            <ListDetail
              list={openList}
              items={items}
              taggable={taggable}
              onAdd={addItem}
              onToggle={toggleItem}
              onUpdate={updateItem}
              onDelete={deleteItem}
              onReorder={reorderItems}
              onShare={() => setShareOpenFor(openList)}
              onOpenLinkedEvent={onOpenLinkedEvent}
              onItemsRefresh={() => loadItems(openListId)}
              onSetListMembers={(ids) => setListMembers(openList.id, ids)}
              onSetListDueDate={(d) => setListDueDate(openList.id, d)}
              onSetListLinkedEvent={(eid) => setListLinkedEvent(openList.id, eid)}
            />
          )}
        </div>

        {shareOpenFor && (
          <ShareDialog
            list={shareOpenFor}
            onClose={() => setShareOpenFor(null)}
            onRotate={rotateShare}
          />
        )}
      </div>
    </div>
  );
}

// =========================================================================
// Lists index
// =========================================================================

function ListsIndex({ lists, busy, onCreate, onOpen, onRename, onDelete, onShare, onReorder, taggable = [] }) {
  const [newTitle, setNewTitle] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [dragId, setDragId] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newTitle.trim() || busy) return;
    await onCreate(newTitle);
    setNewTitle("");
  };

  // Reorder list helpers — vanilla HTML5 DnD. Same approach the members
  // grid uses; works with mouse + touch (long-press to drag on iOS).
  const moveDraggedTo = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const from = lists.findIndex((l) => l.id === dragId);
    const to = lists.findIndex((l) => l.id === targetId);
    if (from < 0 || to < 0) return;
    const next = [...lists];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder(next);
  };

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className="flex items-center gap-2"
        data-testid="todo-create-list-form"
      >
        <input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="New list — e.g. Camping packing"
          maxLength={60}
          className="flex-1 ff-input"
          data-testid="todo-new-list-input"
        />
        <button
          type="submit"
          disabled={!newTitle.trim() || busy}
          className="ff-btn ff-btn-primary inline-flex items-center gap-1 disabled:opacity-50"
          data-testid="todo-new-list-submit"
        >
          <Plus className="w-4 h-4" strokeWidth={2} /> Add
        </button>
      </form>

      {lists === null ? (
        <div className="text-[color:var(--ink-3)] text-sm">Loading…</div>
      ) : lists.length === 0 ? (
        <div className="ff-card p-5 text-center" data-testid="todo-empty-state">
          <div className="font-display-italic text-2xl">No lists yet.</div>
          <p className="text-sm text-[color:var(--ink-3)] mt-2 leading-relaxed">
            Lists are perfect for shopping, packing, books to read, the kids'
            sleepover supplies — anything you'd otherwise text the family group
            chat about.
          </p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="todo-lists-index">
          {lists.map((l) => {
            const total = l.item_count || 0;
            const done = l.done_count || 0;
            const remaining = Math.max(0, total - done);
            return (
              <li
                key={l.id}
                draggable
                onDragStart={() => setDragId(l.id)}
                onDragEnd={() => setDragId(null)}
                onDragOver={(e) => {
                  e.preventDefault();
                  moveDraggedTo(l.id);
                }}
                className={`ff-card p-3 flex items-center gap-2 ${
                  dragId === l.id ? "opacity-40" : ""
                }`}
                data-testid={`todo-list-row-${l.id}`}
              >
                <span
                  className="text-[color:var(--ink-3)] cursor-grab active:cursor-grabbing select-none touch-none shrink-0"
                  aria-label="Drag to reorder"
                  data-testid={`todo-list-drag-${l.id}`}
                  onTouchStart={(e) => {
                    // Some mobile browsers won't start a drag from a
                    // touch — set the dragId here so a long-press +
                    // drag still works as a manual swap onTouchMove.
                    setDragId(l.id);
                    e.stopPropagation();
                  }}
                  onTouchEnd={() => setDragId(null)}
                >
                  <GripVertical className="w-4 h-4" strokeWidth={1.75} />
                </span>
                {renamingId === l.id ? (
                  <input
                    autoFocus
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={async () => {
                      await onRename(l.id, renameValue);
                      setRenamingId(null);
                    }}
                    onKeyDown={async (e) => {
                      if (e.key === "Enter") {
                        await onRename(l.id, renameValue);
                        setRenamingId(null);
                      }
                      if (e.key === "Escape") setRenamingId(null);
                    }}
                    className="flex-1 ff-input"
                    maxLength={60}
                    data-testid={`todo-list-rename-input-${l.id}`}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => onOpen(l.id)}
                    className="flex-1 min-w-0 text-left"
                    data-testid={`todo-list-open-${l.id}`}
                  >
                    <div className="text-[15px] leading-tight truncate">{l.title}</div>
                    <div className="text-[12px] text-[color:var(--ink-3)] mt-0.5 flex items-center gap-2 flex-wrap">
                      <span>
                        {total === 0
                          ? "Empty"
                          : remaining === 0
                          ? `All ${total} done ✨`
                          : `${remaining} of ${total} to go`}
                      </span>
                      {(l.member_ids || []).length > 0 && (
                        <span
                          className="inline-flex items-center gap-1"
                          data-testid={`todo-list-row-for-${l.id}`}
                        >
                          <span className="text-[10px] uppercase tracking-[0.08em] opacity-60">
                            for
                          </span>
                          <span className="inline-flex items-center -space-x-1">
                            {(l.member_ids || [])
                              .map((mid) => taggable.find((m) => m.id === mid))
                              .filter(Boolean)
                              .slice(0, 4)
                              .map((m) => (
                                <span
                                  key={m.id}
                                  className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[color:var(--bg-soft)] border border-[color:var(--line-soft)] text-[12px] leading-none overflow-hidden"
                                  title={m.name}
                                >
                                  {m.avatar_image ? (
                                    <img
                                      src={m.avatar_image}
                                      alt=""
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    m.avatar_emoji || "🙂"
                                  )}
                                </span>
                              ))}
                          </span>
                        </span>
                      )}
                      {(() => {
                        const due = formatDueLabel(l.due_date);
                        if (!due) return null;
                        return (
                          <span
                            className={`px-1.5 py-0.5 rounded-full text-[11px] ${
                              due.tone === "danger"
                                ? "bg-[#F6D6DA] text-[#7A2E3A]"
                                : due.tone === "warn"
                                ? "bg-[#FFE2C7] text-[#7A4A1F]"
                                : "bg-[color:var(--bg-soft)]"
                            }`}
                            data-testid={`todo-list-row-due-${l.id}`}
                          >
                            {due.label}
                          </span>
                        );
                      })()}
                    </div>
                  </button>
                )}
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => onShare(l)}
                    className="p-1.5 rounded-full hover:bg-[color:var(--bg-soft)]"
                    aria-label="Share list"
                    data-testid={`todo-list-share-${l.id}`}
                  >
                    <Share2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setRenamingId(l.id);
                      setRenameValue(l.title);
                    }}
                    className="p-1.5 rounded-full hover:bg-[color:var(--bg-soft)]"
                    aria-label="Rename list"
                    data-testid={`todo-list-rename-${l.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(l)}
                    className="p-1.5 rounded-full hover:bg-[color:var(--bg-soft)] text-[color:var(--ink-3)]"
                    aria-label="Delete list"
                    data-testid={`todo-list-delete-${l.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

// =========================================================================
// List detail
// =========================================================================

const todayIso = () => new Date().toISOString().slice(0, 10);

function formatDueLabel(due) {
  // Tiny human-friendly format for the per-item label. We deliberately
  // don't import date-fns just for this — the "today/tomorrow/<weekday>"
  // logic stays here as a 6-line helper.
  if (!due) return null;
  const t = todayIso();
  if (due === t) return { label: "Due today", tone: "warn" };
  const tomorrow = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  })();
  if (due === tomorrow) return { label: "Due tomorrow", tone: "soft" };
  if (due < t) return { label: `Overdue · ${due}`, tone: "danger" };
  return { label: `Due ${due}`, tone: "soft" };
}

function ListDetail({ list, items, taggable, onAdd, onToggle, onUpdate, onDelete, onReorder, onShare, onSetListMembers, onSetListDueDate, onSetListLinkedEvent }) {
  const [newText, setNewText] = useState("");
  const inputRef = useRef(null);
  const [draggingItemId, setDraggingItemId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [linkedEvent, setLinkedEvent] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [eventPickerOpen, setEventPickerOpen] = useState(false);
  const [pickerEvents, setPickerEvents] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);

  // Fetch the linked event when this list has one.
  useEffect(() => {
    let cancelled = false;
    if (!list?.linked_event_id) {
      setLinkedEvent(null);
      return;
    }
    api.upcomingEvents().then((rows) => {
      if (cancelled) return;
      const ev = rows.find((e) => e.id === list.linked_event_id);
      setLinkedEvent(ev || null);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [list?.linked_event_id]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const t = newText.trim();
    if (!t) return;
    // List-level model: items are just text + done flag. Person tags
    // and due dates live on the LIST itself.
    await onAdd({ text: t, member_ids: [], due_date: null });
    setNewText("");
    inputRef.current?.focus();
  };

  const moveDraggedTo = (targetId) => {
    if (!items || !dragId || dragId === targetId) return;
    const from = items.findIndex((i) => i.id === dragId);
    const to = items.findIndex((i) => i.id === targetId);
    if (from < 0 || to < 0) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder(next);
  };

  if (!list) return null;
  const total = (items || []).length;
  const doneCount = (items || []).filter((i) => i.done).length;

  return (
    <>
      {linkedEvent && (
        <div
          className="ff-card p-3 flex items-center gap-3 bg-[color:var(--bg-soft)]"
          data-testid={`todo-linked-event-banner-${linkedEvent.id}`}
        >
          <button
            type="button"
            onClick={() => onOpenLinkedEvent && onOpenLinkedEvent(linkedEvent.id)}
            className="flex items-center gap-3 flex-1 min-w-0 text-left"
            data-testid={`todo-linked-event-open-${linkedEvent.id}`}
            aria-label="Open this event in the calendar"
          >
            <span className="text-2xl leading-none flex-shrink-0" aria-hidden="true">
              📅
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] uppercase tracking-[0.06em] text-[color:var(--ink-3)]">
                For
              </div>
              <div className="text-[15px] font-display leading-tight tracking-tight truncate">
                {linkedEvent.title}
              </div>
              <div className="text-[11px] text-[color:var(--ink-3)] mt-0.5">
                {(() => {
                  if (!linkedEvent.starts_at) return null;
                  const start = new Date(linkedEvent.starts_at);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const diffDays = Math.round(
                    (start - today) / (1000 * 60 * 60 * 24)
                  );
                  if (diffDays === 0) return "Today";
                  if (diffDays === 1) return "Tomorrow";
                  if (diffDays < 0) return `${Math.abs(diffDays)} day(s) ago`;
                  return `In ${diffDays} days`;
                })()}
              </div>
            </div>
          </button>
          {onSetListLinkedEvent && (
            <button
              type="button"
              onClick={() => onSetListLinkedEvent("")}
              className="ff-btn text-sm h-8 flex-shrink-0"
              data-testid={`todo-unlink-event-${list.id}`}
              title="Unlink from this event"
              aria-label="Unlink from this event"
            >
              <X className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
          )}
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <div className="text-[12px] text-[color:var(--ink-3)]" data-testid="todo-list-progress">
          {total === 0
            ? "Empty list"
            : doneCount === total
            ? `All ${total} done ✨`
            : `${total - doneCount} of ${total} left`}
        </div>
        <button
          type="button"
          onClick={onShare}
          className="ff-btn inline-flex items-center gap-1"
          data-testid="todo-detail-share-btn"
        >
          <Share2 className="w-3.5 h-3.5" strokeWidth={2} /> Share
        </button>
      </div>

      {/* List-level "For" — tags people to the WHOLE list. Person tags
          and due dates are list-scope only; per-item tagging was removed
          (2026-04) because users kept treating the panel as list-scope
          regardless. The list IS the unit. */}
      {onSetListMembers && (
        <div
          className="ff-card p-3"
          data-testid="todo-list-for"
        >
          <MemberPicker
            label="For (this whole list)"
            members={taggable}
            selected={list.member_ids || []}
            onChange={onSetListMembers}
            testidPrefix="todo-list-for-tag"
          />
        </div>
      )}

      {onSetListDueDate && (
        <div className="ff-card p-3" data-testid="todo-list-due">
          <div className="label-tiny mb-2">Due (this whole list)</div>
          <DueDatePicker
            value={list.due_date || ""}
            onChange={onSetListDueDate}
            testid="todo-list-due-input"
          />
        </div>
      )}

      {/* Attach to a calendar event — only shown when not already
          attached. Tapping opens an inline event picker that pulls
          upcoming events on demand. Linking is a single tap; the
          banner above replaces this picker on next render. */}
      {onSetListLinkedEvent && !linkedEvent && (
        <div className="ff-card p-3" data-testid="todo-list-link-event">
          {!eventPickerOpen ? (
            <button
              type="button"
              onClick={async () => {
                setEventPickerOpen(true);
                setPickerLoading(true);
                try {
                  const rows = await api.upcomingEvents(40);
                  setPickerEvents(rows || []);
                } catch (e) {
                  toast.error("Couldn't load events");
                } finally {
                  setPickerLoading(false);
                }
              }}
              className="w-full flex items-center justify-between gap-2 text-left"
              data-testid="todo-list-attach-btn"
            >
              <span className="label-tiny">Attach to a calendar event</span>
              <span className="label-tiny text-[color:var(--ink-3)]">▸</span>
            </button>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="label-tiny">Pick an event</div>
                <button
                  type="button"
                  onClick={() => setEventPickerOpen(false)}
                  className="label-tiny text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"
                  data-testid="todo-list-attach-close"
                >
                  Close
                </button>
              </div>
              {pickerLoading ? (
                <div className="text-[12px] text-[color:var(--ink-3)] py-2">Loading…</div>
              ) : pickerEvents.length === 0 ? (
                <div className="text-[12px] text-[color:var(--ink-3)] py-2">
                  No upcoming events. Add one in the calendar first.
                </div>
              ) : (
                <ul
                  className="max-h-[280px] overflow-auto divide-y divide-[color:var(--line-soft)]"
                  data-testid="todo-list-attach-events"
                >
                  {pickerEvents.map((ev) => {
                    const start = ev.starts_at ? new Date(ev.starts_at) : null;
                    const fmt = start
                      ? start.toLocaleDateString("en-US", {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                        })
                      : "";
                    return (
                      <li key={ev.id}>
                        <button
                          type="button"
                          onClick={async () => {
                            await onSetListLinkedEvent(ev.id);
                            setEventPickerOpen(false);
                          }}
                          className="w-full flex items-center justify-between gap-3 py-2 text-left hover:bg-[color:var(--bg-soft)] -mx-1 px-1 rounded"
                          data-testid={`todo-list-attach-pick-${ev.id}`}
                        >
                          <span className="text-[14px] truncate">{ev.title}</span>
                          <span className="label-tiny text-[color:var(--ink-3)] flex-shrink-0">
                            {fmt}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      <form onSubmit={submit} className="space-y-2" data-testid="todo-add-item-form">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            placeholder="Add an item"
            maxLength={200}
            className="flex-1 ff-input"
            data-testid="todo-new-item-input"
          />
          <button
            type="submit"
            disabled={!newText.trim()}
            className="ff-btn ff-btn-primary inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="todo-new-item-submit"
          >
            <Plus className="w-4 h-4" strokeWidth={2} /> Add
          </button>
        </div>
      </form>

      {items === null ? (
        <div className="text-[color:var(--ink-3)] text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-[color:var(--ink-3)] text-sm">
          Nothing yet — add the first thing above.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="todo-items-list">
          {items.map((it) => {
            return (
              <li
                key={it.id}
                draggable
                onDragStart={() => setDragId(it.id)}
                onDragEnd={() => setDragId(null)}
                onDragOver={(e) => {
                  e.preventDefault();
                  moveDraggedTo(it.id);
                }}
                className={`ff-card p-3 ${it.done ? "opacity-60" : ""} ${
                  dragId === it.id ? "opacity-40" : ""
                }`}
                data-testid={`todo-item-row-${it.id}`}
              >
                <div className="flex items-center gap-3">
                  <span
                    className="text-[color:var(--ink-3)] cursor-grab active:cursor-grabbing select-none touch-none shrink-0"
                    aria-label="Drag to reorder"
                    data-testid={`todo-item-drag-${it.id}`}
                  >
                    <GripVertical className="w-4 h-4" strokeWidth={1.75} />
                  </span>
                  <button
                    type="button"
                    onClick={() => onToggle(it)}
                    className={`w-8 h-8 shrink-0 rounded-full border flex items-center justify-center transition-colors ${
                      it.done
                        ? "bg-[#CDE6D2] border-[#7db389]"
                        : "border-[color:var(--line)] hover:bg-[color:var(--bg-soft)]"
                    }`}
                    aria-label={it.done ? "Mark not done" : "Mark done"}
                    data-testid={`todo-item-toggle-${it.id}`}
                  >
                    {it.done ? <Check className="w-4 h-4" strokeWidth={2.5} /> : null}
                  </button>
                  {editingId === it.id ? (
                    <InlineRename
                      initial={it.text}
                      onSave={async (v) => {
                        if (v && v.trim() && v.trim() !== it.text) {
                          await onUpdate(it, { text: v.trim() });
                        }
                        setEditingId(null);
                      }}
                      onCancel={() => setEditingId(null)}
                      testid={`todo-item-edit-input-${it.id}`}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setEditingId(it.id)}
                      className="flex-1 min-w-0 text-left"
                      data-testid={`todo-item-text-${it.id}`}
                    >
                      <div
                        className={`text-[15px] leading-tight ${
                          it.done
                            ? "line-through text-[color:var(--ink-2)]"
                            : "text-[color:var(--ink)]"
                        }`}
                      >
                        {it.text}
                      </div>
                      {it.done && it.done_by === "share" && (
                        <div className="text-[11px] text-[color:var(--ink-3)] mt-1">
                          Ticked from the shared link
                        </div>
                      )}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onDelete(it)}
                    className="p-1.5 rounded-full hover:bg-[color:var(--bg-soft)] text-[color:var(--ink-3)] shrink-0"
                    aria-label="Delete item"
                    data-testid={`todo-item-delete-${it.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

// =========================================================================
// Inline rename — extracted so its keydown/blur handlers stay clean.
// =========================================================================

function InlineRename({ initial, onSave, onCancel, testid }) {
  const [val, setVal] = useState(initial);
  return (
    <input
      autoFocus
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => onSave(val)}
      onKeyDown={(e) => {
        if (e.key === "Enter") onSave(val);
        if (e.key === "Escape") onCancel();
      }}
      className="flex-1 ff-input"
      maxLength={200}
      data-testid={testid}
    />
  );
}

// =========================================================================
// Member picker — used both for new items and per-item updates.
// =========================================================================

function MemberPicker({ members, selected, onChange, testidPrefix, label = "Tag people" }) {
  if (!members || members.length === 0) {
    return (
      <div className="text-[12px] text-[color:var(--ink-3)]">
        Add family members to start tagging items.
      </div>
    );
  }
  const isSelected = (id) => selected.includes(id);
  const toggle = (id) => {
    if (isSelected(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  };
  return (
    <div>
      <div className="label-tiny mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {members.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => toggle(m.id)}
            className={`text-[12px] inline-flex items-center gap-1 px-2 py-1 rounded-full border transition-colors ${
              isSelected(m.id)
                ? "bg-[#CDE6D2] border-[#7db389]"
                : "border-[color:var(--line)] hover:bg-[color:var(--bg-soft)]"
            }`}
            data-testid={`${testidPrefix}-${m.id}`}
          >
            {m.avatar_image ? (
              <img
                src={m.avatar_image}
                alt=""
                className="w-4 h-4 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <span className="text-base leading-none">{m.avatar_emoji || "🙂"}</span>
            )}
            <span>{m.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// =========================================================================
// Due-date picker — type=date, plus quick "Today / Tomorrow / Clear".
// =========================================================================

function DueDatePicker({ value, onChange, testid }) {
  const today = todayIso();
  const tomorrow = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  })();
  return (
    <div>
      <div className="label-tiny mb-1.5">Due date</div>
      <div className="flex flex-wrap items-center gap-1.5">
        <input
          type="date"
          value={value || ""}
          min={today}
          onChange={(e) => onChange(e.target.value || "")}
          className="ff-input text-[13px] py-1.5"
          data-testid={testid}
        />
        <button
          type="button"
          onClick={() => onChange(today)}
          className="text-[12px] px-2 py-1 rounded-full border border-[color:var(--line)] hover:bg-[color:var(--bg-soft)]"
          data-testid={`${testid}-today`}
        >
          Today
        </button>
        <button
          type="button"
          onClick={() => onChange(tomorrow)}
          className="text-[12px] px-2 py-1 rounded-full border border-[color:var(--line)] hover:bg-[color:var(--bg-soft)]"
          data-testid={`${testid}-tomorrow`}
        >
          Tomorrow
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="text-[12px] px-2 py-1 rounded-full border border-[color:var(--line)] text-[color:var(--ink-3)] hover:bg-[color:var(--bg-soft)]"
            data-testid={`${testid}-clear`}
          >
            Clear
          </button>
        )}
      </div>
      {value && (
        <p className="text-[11px] text-[color:var(--ink-3)] mt-1.5 leading-relaxed">
          We&apos;ll ping tagged people at 8 PM the night before and around 7 AM (weekdays) / 8 AM (weekends) on the day.
        </p>
      )}
    </div>
  );
}

// =========================================================================
// Share dialog (copy link · open print view · rotate)
// =========================================================================

function ShareDialog({ list, onClose, onRotate }) {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://famfeel.com";
  const shareUrl = `${origin}/lists/${list.share_token}`;
  const printUrl = `${origin}/lists/${list.share_token}/print`;
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — long-press to copy manually");
    }
  };

  return (
    <div
      className="absolute inset-0 z-20 flex items-end md:items-center justify-center bg-[color:var(--ink)]/35"
      onClick={onClose}
      data-testid="todo-share-dialog"
    >
      <div
        className="bg-[color:var(--bg)] w-full md:max-w-md md:rounded-[18px] rounded-t-[24px] p-5 space-y-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="label-tiny">Share</div>
            <h3 className="font-display-italic text-2xl leading-none mt-1 tracking-tight">
              {list.title}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-[color:var(--bg-soft)] flex items-center justify-center"
            aria-label="Close share menu"
            data-testid="todo-share-close"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        <p className="text-sm text-[color:var(--ink-3)] leading-relaxed">
          Anyone with this link can see the list and tick items off — no FamFeel
          login required. Perfect for the babysitter, grandma, or the cleaner.
          They can't add, edit, or delete items, and the list never reveals who
          on the family is tagged to which item.
        </p>

        <div className="ff-card p-3 flex items-center gap-2">
          <code
            className="flex-1 text-[12px] break-all text-[color:var(--ink-2)]"
            data-testid="todo-share-url"
          >
            {shareUrl}
          </code>
          <button
            type="button"
            onClick={copy}
            className="ff-btn inline-flex items-center gap-1 shrink-0"
            data-testid="todo-share-copy-btn"
          >
            <LinkIcon className="w-3.5 h-3.5" strokeWidth={2} />
            {copied ? "Copied" : "Copy"}
          </button>
        </div>

        <div className="flex flex-col gap-2">
          <a
            href={printUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="ff-btn inline-flex items-center justify-center gap-2"
            data-testid="todo-share-print-btn"
          >
            <Printer className="w-4 h-4" strokeWidth={2} /> Open print view
          </a>
          <button
            type="button"
            onClick={() => onRotate(list)}
            className="ff-btn inline-flex items-center justify-center gap-2 text-[color:var(--ink-3)]"
            data-testid="todo-share-rotate-btn"
          >
            <RefreshCw className="w-4 h-4" strokeWidth={2} /> Rotate link
          </button>
        </div>
        <p className="text-[11px] text-[color:var(--ink-3)] leading-relaxed">
          Rotating the link revokes the old one. Use this if you shared it with
          someone and you'd rather they didn't keep tapping things off.
        </p>
      </div>
    </div>
  );
}
